/* eslint-disable no-unused-vars */
import { useEffect, useState } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { useNavigate, useParams } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useLoader from "../../../hooks/useLoader";
import useForm from "../../../hooks/useForm";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import { useQuery } from "@tanstack/react-query";
import { detailsTransactionFields } from "../../../constants/forms/almoxarifado/transactionFields";
import DetailsSectionRenderer from "../../../components/DetailsSectionRenderer";
import { setDefaultFieldValues } from "../../../utils/objectUtils";
import { formatDate } from "../../../utils/formatDate";
import { useSectionFields } from "../../../hooks/useSectionFields";
import ListHeader from "../../../components/ListHeader";
import { usePermissions } from "../../../hooks/usePermissions";
import useUrlFilters from "../../../hooks/useUrlFilters";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import TableWithInactiveFilter from "../../../components/table/TableWithInactiveFilter";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const DetailsTransactionPage = () => {
  const { id } = useParams();
  const { canAccess } = usePermissions();
  const navigate = useNavigate();
  const { getByColumn: baseServiceGetByColumn, get: baseServiceGet } =
    useBaseService(navigate);
  const { showLoader, hideLoader } = useLoader();
  const { updateField, removeFields } = useSectionFields();

  const [transactionSection] = detailsTransactionFields;
  const [transactionFieldsState, setTransactionFieldsState] = useState(
    transactionSection.fields,
  );
  const { formData, setFormData } = useForm(
    setDefaultFieldValues(detailsTransactionFields),
    detailsTransactionFields,
  );

  // Filtros e Paginação para PRODUTOS
  const {
    filters: prodFilters,
    pagination: prodPagination,
    changePage: prodChangePage,
    setPagination: prodSetPagination,
  } = useUrlFilters({ deleted_at: false }, "transaction_products");

  // Filtros e Paginação para EQUIPAMENTOS
  const {
    filters: equipFilters,
    pagination: equipPagination,
    changePage: equipChangePage,
    setPagination: equipSetPagination,
  } = useUrlFilters({ deleted_at: false }, "transaction_equipments");

  const [itemsColumns, setItemsColumns] = useState([]);
  const [equipmentsColumns, setEquipmentsColumns] = useState([]);

  const { data: transactionData, isLoading: isLoadingTransactionData } =
    useQueryWithLoader({
      queryKey: ["almox_transaction", id],
      queryFn: async () => {
        showLoader();
        const response = await baseServiceGetByColumn(
          entities.transactions.getById(id),
        );
        return response.result;
      },
      select: (data) => {
        return {
          transaction_type: data.type.name,
          is_internal_exit: data.is_internal_exit,
          transaction_date: formatDate({
            rawDate: data.transaction_date,
            showTime: false,
          }),
          supplier: data.supplier?.name || "-",
          sender: data.sender?.name || "-",
          customer: data.customer?.name || "-",
          receiver: data.receiver?.name || "-",
          doc_type: data.document_type?.name || "-",
          doc_number: data.doc_number,
          buyer: data.buyer?.name || "-",
          user: data.user.name,
          doc_date: formatDate({ rawDate: data.doc_date, showTime: false }),
          purchase_date: formatDate({
            rawDate: data.purchase_date,
            showTime: false,
          }),
          details: data.details,
        };
      },
    });
  const isInternalExit = transactionData?.is_internal_exit;

  // Query de PRODUTOS
  const {
    data: productsResult,
    isLoading: isLoadingProducts,
    isFetching: isFetchingProducts,
  } = useQueryWithLoader({
    queryKey: ["almox_transaction_products", id, prodPagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl({
        product_id: { $notnull: true },
        equipment_id: { $eq: null },
      });

      const params = {
        filters: filtersTransformed,
        page: prodPagination.page,
        perPage: prodPagination.perPage,
      };

      const response = await baseServiceGet(
        entities.transactions.items(id),
        params,
      );
      return response.result;
    },
    enabled: !!id,
    select: (data) => ({
      items: data.data.map((item) => ({
        ...item,
        product: item.product?.name || "-",
        category_id: item.category?.name || "-",
        condition_id: item.condition?.name || "-",
        address_id: item.address?.formatted_address || "-",
        location_id: item.location?.formatted_location || "-",
        warranty_date: formatDate({
          rawDate: item.warranty_date,
          showTime: false,
        }),
      })),
      totalPages: data.last_page,
    }),
  });

  // Query de EQUIPAMENTOS
  const {
    data: equipmentsResult,
    isLoading: isLoadingEquipments,
    isFetching: isFetchingEquipments,
  } = useQueryWithLoader({
    queryKey: ["almox_transaction_equipments", id, equipPagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl({
        product_id: { $eq: null },
        equipment_id: { $notnull: true },
      });

      const params = {
        filters: filtersTransformed,
        page: equipPagination.page,
        perPage: equipPagination.perPage,
      };

      const response = await baseServiceGet(
        entities.transactions.items(id),
        params,
      );
      return response.result;
    },
    enabled: !!id,
    select: (data) => ({
      items: data.data.map((equip) => ({
        ...equip,
        equipment: `${equip.equipment?.number} - ${equip.equipment?.name}`,
        address_id: equip.address?.formatted_address || "-",
        location_id: equip.location?.formatted_location || "-",
      })),
      totalPages: data.last_page,
    }),
  });

  useEffect(() => {
    let updatedFields = [...transactionSection.fields];
    if (!transactionData) return;

    const type = transactionData.transaction_type
      ?.trim()
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (isInternalExit) {
      updatedFields = removeFields({
        arrayDependente: updatedFields,
        field_id: ["transaction.amount_total", "transaction.amount_discount"],
      });
    }

    if (isInternalExit) {
      setItemsColumns([
        { key: "product", label: "Insumo" },
        { key: "category_id", label: "Categoria" },
        { key: "condition_id", label: "Condição" },
        { key: "address_id", label: "Endereço" },
        { key: "location_id", label: "Localização" },
        {
          key: "unit_cost",
          label: "Preço Unitário",
          numeric: true,
          isMoney: true,
          align: "right",
        },
        {
          key: "quantity",
          label: "Quantidade",
          numeric: true,
          align: "right",
        },
      ]);
      setEquipmentsColumns([]);
    }

    switch (type) {
      case "ENTRADA":
        setItemsColumns([
          { key: "product", label: "Produto" },
          { key: "category_id", label: "Categoria" },
          { key: "condition_id", label: "Condição" },
          { key: "address_id", label: "Endereço" },
          { key: "location_id", label: "Localização" },
          { key: "warranty_date", label: "Data de Garantia" },
          {
            key: "unit_cost",
            label: "Preço Unitário",
            numeric: true,
            isMoney: true,
            align: "right",
          },
          {
            key: "quantity",
            label: "Quantidade",
            numeric: true,
            align: "right",
          },
        ]);
        updatedFields = removeFields({
          arrayDependente: updatedFields,
          field_id: [
            "transaction.customer",
            "transaction.receiver",
            "transaction.sender",
          ],
        });
        setTransactionFieldsState(updatedFields);
        break;

      case "SAIDA":
      case "TRANSFERENCIA":
        setItemsColumns([
          { key: "product", label: "Insumo" },
          { key: "category_id", label: "Categoria" },
          { key: "condition_id", label: "Condição" },
          { key: "address_id", label: "Endereço" },
          { key: "location_id", label: "Localização" },
          {
            key: "unit_cost",
            label: "Preço Unitário",
            numeric: true,
            isMoney: true,
            align: "right",
          },
          {
            key: "quantity",
            label: "Quantidade",
            numeric: true,
            align: "right",
          },
        ]);

        setEquipmentsColumns(
          isInternalExit
            ? []
            : [
                { key: "equipment", label: "Equipamento" },
                { key: "address_id", label: "Endereço" },
                { key: "location_id", label: "Localização" },
              ],
        );

        if (type === "TRANSFERENCIA") {
          updatedFields = removeFields({
            arrayDependente: updatedFields,
            field_id: ["transaction.customer"],
          });

          updatedFields = updateField({
            arrayDependente: updatedFields,
            field_id: ["transaction.user"],
            updates: {
              fullWidth: true,
            },
          });
        } else if (type === "SAIDA") {
          updatedFields = removeFields({
            arrayDependente: updatedFields,
            field_id: ["transaction.receiver"],
          });

          updatedFields = updateField({
            arrayDependente: updatedFields,
            field_id: ["transaction.user"],
            updates: {
              fullWidth: true,
            },
          });
        }

        updatedFields = removeFields({
          arrayDependente: updatedFields,
          field_id: [
            "transaction.transaction_date",
            "transaction.doc_type",
            "transaction.supplier",
            "transaction.doc_number",
            "transaction.doc_date",
            "transaction.purchase_date",
            "transaction.buyer",
          ],
        });

        updatedFields = updateField({
          arrayDependente: updatedFields,
          field_id: ["transaction.transaction_type"],
          updates: {
            fullWidth: true,
          },
        });
        setTransactionFieldsState(updatedFields);
        break;

      case "USO INTERNO":
        setItemsColumns([
          { key: "product", label: "Insumo" },
          { key: "category_id", label: "Categoria" },
          { key: "condition_id", label: "Condição" },
          { key: "address_id", label: "Endereço" },
          { key: "location_id", label: "Localização" },
          {
            key: "unit_cost",
            label: "Preço Unitário",
            numeric: true,
            isMoney: true,
            align: "right",
          },
          {
            key: "quantity",
            label: "Quantidade",
            numeric: true,
            align: "right",
          },
        ]);
        setEquipmentsColumns(
          isInternalExit
            ? []
            : [
                { key: "equipment", label: "Equipamento" },
                { key: "address_id", label: "Endereço" },
                { key: "location_id", label: "Localização" },
              ],
        );

        if (type === "TRANSFERENCIA" || type === "USO INTERNO") {
          updatedFields = removeFields({
            arrayDependente: updatedFields,
            field_id: ["transaction.customer"],
          });
        } else if (type === "SAIDA") {
          updatedFields = removeFields({
            arrayDependente: updatedFields,
            field_id: ["transaction.receiver"],
          });
        }

        updatedFields = removeFields({
          arrayDependente: updatedFields,
          field_id: [
            "transaction.doc_type",
            "transaction.supplier",
            "transaction.doc_number",
            "transaction.doc_date",
            "transaction.purchase_date",
            "transaction.buyer",
          ],
        });

        updatedFields = updateField({
          arrayDependente: updatedFields,
          field_id: ["transaction.transaction_type"],
          updates: {
            fullWidth: true,
          },
        });
        setTransactionFieldsState(updatedFields);
        break;

      default:
        break;
    }
  }, [
    isInternalExit,
    removeFields,
    transactionData,
    transactionSection.fields,
    updateField,
  ]);

  useEffect(() => {
    if (!isLoadingTransactionData) {
      hideLoader();
      setFormData(transactionData);
    }
  }, [hideLoader, isLoadingTransactionData, setFormData, transactionData]);

  return (
    <MainLayout>
      <PageHeader
        title={`Detalhes da Transação ${transactionData?.doc_number || ""} - ${
          transactionData?.transaction_type || ""
        }`}
        showBackButton={true}
        backUrl="/almoxarifado/transacoes/entrada"
      />
      <div className="container-fluid p-1">
        <DetailsSectionRenderer
          formData={formData}
          sections={[
            { ...detailsTransactionFields, fields: transactionFieldsState },
          ]}
          readOnly
        />
        {/* SEÇÃO DE PRODUTOS */}
        {(transactionData?.transaction_type !== "USO INTERNO" ||
          isInternalExit) && (
          <div className="mt-4">
            <div className="my-2">
              <ListHeader
                title="Insumos"
                buttonIsNotVisible={true}
                canAccess={canAccess}
              />
            </div>
            <TableWithInactiveFilter
              type="generic" // Define para usar a GenericTable internamente
              data={productsResult?.items || []}
              columns={itemsColumns}
              totalPages={productsResult?.totalPages || 1}
              currentPage={prodPagination.page - 1}
              onPageChange={prodChangePage}
              rowsPerPage={prodPagination.perPage}
              onRowsPerPageChange={(event) => {
                const newPerPage = +event.target.value;
                prodSetPagination({ page: 1, perPage: newPerPage });
              }}
              showFilterInactive={false}
              showActions={false}
              isLoading={isLoadingProducts}
              isFetching={isFetchingProducts}
            />
          </div>
        )}
        {/* SEÇÃO DE EQUIPAMENTOS */}
        {transactionData?.transaction_type !== "ENTRADA" &&
          !isInternalExit && (
          <div className="mt-4">
            <div className="my-2">
              <ListHeader
                title="Equipamentos"
                buttonIsNotVisible={true}
                canAccess={canAccess}
              />
            </div>
            <TableWithInactiveFilter
              type="generic"
              data={equipmentsResult?.items || []}
              columns={equipmentsColumns}
              totalPages={equipmentsResult?.totalPages || 1}
              currentPage={equipPagination.page - 1}
              onPageChange={equipChangePage}
              rowsPerPage={equipPagination.perPage}
              onRowsPerPageChange={(event) => {
                const newPerPage = +event.target.value;
                equipSetPagination({ page: 1, perPage: newPerPage });
              }}
              showFilterInactive={false}
              showActions={false}
              isLoading={isLoadingEquipments}
              isFetching={isFetchingEquipments}
            />
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default DetailsTransactionPage;
